import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CheckoutModal from "@/components/CheckoutModal";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [params, setParams] = useSearchParams();
  const subParam = params.get("sub");
  const navigate = useNavigate();
  const { products, categories } = useCart();
  const { t, isAr } = useLanguage();
  const [visibleCount, setVisibleCount] = useState(30);
  const [heroIdx, setHeroIdx] = useState(0);

  const cat = categories.find((c) => c.id === categoryId);
  const heroes = cat ? (cat.heroes?.length ? cat.heroes : [cat.hero || cat.image]) : [];

  useEffect(() => {
    setHeroIdx(0);
    if (heroes.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroes.length), 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, heroes.length]);
  const catProducts = useMemo(
    () => products.filter((p) => p.category === categoryId),
    [products, categoryId]
  );
  const filtered = subParam ? catProducts.filter((p) => p.subcategory === subParam) : catProducts;
  const visible = filtered.slice(0, visibleCount);
  const newArrivals = useMemo(() => catProducts.filter((p) => p.is_new).slice(0, 12), [catProducts]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setVisibleCount(30);
  }, [categoryId, subParam]);

  useEffect(() => {
    if (cat) document.title = `${isAr ? cat.name_ar : cat.name} | Cambroo General Trading LLC`;
    return () => { document.title = "Cambroo — Mobile Accessories in Dubai | Cambroo General Trading LLC"; };
  }, [cat, isAr]);

  if (!cat) {
    return (
      <>
        <Nav />
        <main className="flex min-h-screen items-center justify-center bg-page pt-[124px]">
          <Link to="/" data-testid="category-back" className="text-sm font-semibold text-link hover:underline">
            {t("pdp_back")}
          </Link>
        </main>
      </>
    );
  }

  const setSub = (subId) => {
    setParams(subId ? { sub: subId } : {});
  };

  return (
    <>
      <Nav />
      <main className="min-h-screen bg-page pt-[132px] md:pt-[124px]" data-testid="category-page">
        {/* Category hero banner */}
        <div className="px-4 lg:px-8">
          <div className="relative mx-auto h-48 max-w-7xl overflow-hidden rounded-xl md:h-64" data-testid="category-hero">
            <AnimatePresence mode="sync">
              <motion.img
                key={heroIdx}
                src={heroes[heroIdx % Math.max(heroes.length, 1)]}
                alt={isAr ? cat.name_ar : cat.name}
                data-testid="category-hero-image"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            {heroes.length > 1 && (
              <div className="absolute bottom-3 end-3 z-10 flex gap-1.5">
                {heroes.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`category-hero-dot-${i}`}
                    onClick={() => setHeroIdx(i)}
                    aria-label={`Banner ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === heroIdx ? "w-5 bg-brand" : "w-2 bg-white/60"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category heading */}
        <div className="px-4 pt-6 lg:px-8">
          <div className="mx-auto flex max-w-7xl items-end justify-between gap-3">
            <h1 className="text-xl font-extrabold text-brandink md:text-2xl" data-testid="category-title">
              {isAr ? cat.name_ar : cat.name}
            </h1>
            <p className="shrink-0 text-sm text-muted">
              {catProducts.length} {t("products_count")}
            </p>
          </div>
        </div>

        {/* Subcategory photo tiles */}
        <div className="px-4 pb-6 pt-4 lg:px-8">
          <div className="no-scrollbar mx-auto flex max-w-7xl gap-4 overflow-x-auto pb-1" data-testid="category-sub-tiles">
            {(cat.subs || []).map((s) => {
              const rep = catProducts.find((p) => p.subcategory === s.id);
              const active = subParam === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`category-sub-tile-${s.id}`}
                  onClick={() => setSub(active ? null : s.id)}
                  className={`group flex w-28 shrink-0 flex-col items-center gap-2 rounded-lg border bg-white p-3 transition-shadow hover:shadow-md ${
                    active ? "border-brandink ring-1 ring-brandink" : "border-line"
                  }`}
                >
                  <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-page">
                    {rep?.image && (
                      <img src={rep.image} alt={isAr ? s.name_ar : s.name} loading="lazy" className="max-h-full max-w-full object-contain" />
                    )}
                  </span>
                  <span className="text-center text-[11px] font-semibold leading-tight text-brandink">
                    {isAr ? s.name_ar : s.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* New arrivals rail */}
        {newArrivals.length > 0 && !subParam && (
          <div className="px-4 pb-6 lg:px-8" data-testid="category-new-arrivals">
            <div className="mx-auto max-w-7xl">
              <h2 className="flex items-center gap-2 text-lg font-bold text-brandink">
                <Sparkles className="h-4 w-4 text-link" /> {t("new_arrivals")}
              </h2>
              <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto pb-2">
                {newArrivals.map((p) => (
                  <div key={p.id} className="w-44 shrink-0 sm:w-52">
                    <ProductCard product={p} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Product grid */}
        <div className="px-4 pb-10 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-brandink" data-testid="category-grid-title">
                {subParam
                  ? (() => { const s = cat.subs?.find((x) => x.id === subParam); return s ? (isAr ? s.name_ar : s.name) : ""; })()
                  : `${t("shop_all")} — ${isAr ? cat.name_ar : cat.name}`}
              </h2>
              {subParam && (
                <button
                  data-testid="category-clear-sub"
                  onClick={() => setSub(null)}
                  className="text-xs font-semibold text-link hover:underline"
                >
                  {t("tab_all")}
                </button>
              )}
            </div>
            <motion.div layout className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="category-grid">
              {visible.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </motion.div>
            {filtered.length > visible.length && (
              <div className="mt-6 text-center">
                <button
                  data-testid="category-load-more"
                  onClick={() => setVisibleCount((c) => c + 30)}
                  className="rounded-md border border-line bg-white px-8 py-2.5 text-xs font-bold text-brandink transition-colors hover:border-brandink"
                >
                  {t("load_more")} ({filtered.length - visible.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <CartDrawer />
      <CheckoutModal />
    </>
  );
}
